import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../assets/js/LanguageContext';
import '../assets/styles/ShapeChat.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Identificamos las encuestas por el PIN (el número que el usuario ve en el panel),
// con el id_encuesta interno solo como respaldo si la encuesta aún no tiene PIN.
const surveyRef = (s) => s.pin || s.id_encuesta;

// Cada accion: etiqueta del boton y texto que se manda al asistente. El texto va en el
// idioma de la web, asi el asistente contesta en ese idioma.
const ACTIONS = {
  list: { label: 'chat_action_list', prompt: 'chat_prompt_list' },
  improve: { label: 'chat_action_improve', prompt: 'chat_prompt_improve' },
  interpret: { label: 'chat_action_interpret', prompt: 'chat_prompt_interpret' }
};

// Paginas de los participantes (responder con PIN): ahi el asistente no pinta nada
const isParticipantPage = (pathname) => pathname === '/pin' || pathname === '/welcome' || /^\/\d{4,}\/?$/.test(pathname);

// Modos de presentación del chat:
//   - 'side'    → panel anclado al lateral derecho a altura completa (por defecto)
//   - 'expanded'→ panel lateral más ancho (mejor para leer respuestas largas)
//   - 'widget'  → burbuja flotante en la esquina inferior izquierda
const MODES = ['side', 'expanded', 'widget'];
const MODE_LABELS = { side: 'chat_mode_side', expanded: 'chat_mode_expanded', widget: 'chat_mode_widget' };

// El markdown usa "> " para dos cosas muy distintas: citar literalmente una
// respuesta de la encuesta y avisar de algo ("⚠️ Nota importante: …"). Las
// citas empiezan por comillas, y esa es la pista que usamos para separarlas:
// la cita va en cursiva y la nota, en redonda sobre fondo.
const nodeText = (node) => {
  if (!node) return '';
  if (node.value) return node.value;
  return (node.children || []).map(nodeText).join('');
};

const isCitation = (node) => /^\s*["“«'']/.test(nodeText(node));

// El asistente es para quien tiene cuenta: sin sesion solo podria decir "inicia sesion"
const ShapeChat = ({ isAuthenticated }) => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('widget');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [loadingSurveys, setLoadingSurveys] = useState(false);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  // Al entrar o salir de una cuenta se empieza de cero: ni la conversacion ni la lista de
  // encuestas de una persona deben verse con la sesion de otra
  useEffect(() => {
    setOpen(false);
    setMessages([]);
    setPicker(null);
    setSurveys(null);
    setInput('');
  }, [isAuthenticated]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open, picker, surveys]);

  useEffect(() => {
    if (open && textareaRef.current && !picker) {
      textareaRef.current.focus();
    }
  }, [open, picker]);

  const sendMessage = useCallback(async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setMessages((prev) => [...prev, { role: 'assistant', content: t('chat_login_required'), error: true }]);
      return;
    }

    const historySnapshot = messages;
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '', streaming: true }]);
    setInput('');
    setLoading(true);

    const appendDelta = (delta) => {
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = { ...last, content: (last.content || '') + delta };
        }
        return next;
      });
    };

    const markError = () => {
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = { role: 'assistant', content: t('chat_error'), error: true };
        }
        return next;
      });
    };

    const markDone = () => {
      setMessages((prev) => {
        const next = prev.slice();
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          const { streaming, ...rest } = last;
          next[next.length - 1] = rest;
        }
        return next;
      });
    };

    try {
      const res = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: text, history: historySnapshot })
      });

      if (!res.ok || !res.body) {
        throw new Error(`Error ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finished = false;

      while (!finished) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sep;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const lines = rawEvent.split('\n');
          let event = 'message';
          let data = '';
          for (const ln of lines) {
            if (ln.startsWith('event:')) event = ln.slice(6).trim();
            else if (ln.startsWith('data:')) data += ln.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (event === 'delta' && parsed.text) appendDelta(parsed.text);
            else if (event === 'error') { console.error('Asistente IA:', parsed.error); markError(); finished = true; break; }
            else if (event === 'done') { finished = true; break; }
          } catch (_) { /* ignore */ }
        }
      }

      markDone();
    } catch (err) {
      console.error('Asistente IA:', err);
      markError();
    } finally {
      setLoading(false);
    }
  }, [input, messages, loading, t]);

  const openPicker = useCallback(async (action) => {
    setPicker(action);
    if (surveys !== null) return;
    const token = localStorage.getItem('token');
    if (!token) {
      // Sin guardar una lista vacia: al iniciar sesion se vuelve a pedir
      setMessages((prev) => [...prev, { role: 'assistant', content: t('chat_login_required'), error: true }]);
      return;
    }
    setLoadingSurveys(true);
    try {
      const res = await fetch(`${API_BASE_URL}/ai/my-surveys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setSurveys(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Asistente IA, encuestas:', err);
      setPicker(null);
      setMessages((prev) => [...prev, { role: 'assistant', content: t('chat_surveys_error'), error: true }]);
    } finally {
      setLoadingSurveys(false);
    }
  }, [surveys, t]);

  const handlePickSurvey = (survey) => {
    const action = ACTIONS[picker];
    if (!action) return;
    const text = t(action.prompt)
      .replace('{ref}', surveyRef(survey))
      .replace('{title}', survey.titulo || t('chat_untitled'));
    setPicker(null);
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Envolvemos cada tabla en un contenedor que la encaja dentro del bocadillo
  // (y le da scroll horizontal si trae demasiadas columnas). En modo widget el
  // espacio es justo, así que ofrecemos ampliar el panel justo donde se nota.
  const mdComponents = useMemo(() => ({
    blockquote: ({ node, ...props }) => (
      <blockquote {...props} className={isCitation(node) ? 'is-citation' : 'is-note'} />
    ),
    table: ({ node, ...props }) => (
      <>
        {mode === 'widget' && (
          <button
            type="button"
            className="shape-chat-table-expand"
            onClick={() => setMode('expanded')}
            title={t('chat_expand_table')}
            aria-label={t('chat_expand_table')}
          >
            ⤢
          </button>
        )}
        <div className="shape-chat-table-wrap">
          <table {...props} />
        </div>
      </>
    )
  }), [mode, t]);

  if (!isAuthenticated || isParticipantPage(pathname)) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="shape-chat-toggle"
        onClick={() => setOpen(true)}
        aria-label={t('chat_open_label')}
        title={t('chat_title')}
      >
        <span className="shape-chat-toggle-icon" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="" draggable="false" />
        </span>
      </button>
    );
  }

  // SVG icons inline — sin dependencias y heredan currentColor del botón.
  const iconStroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const IconSide = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" {...iconStroke}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="14" y1="4" x2="14" y2="20" />
    </svg>
  );
  const IconExpand = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" {...iconStroke}>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  );
  const IconWidget = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" {...iconStroke}>
      <rect x="3" y="11" width="12" height="10" rx="2" />
    </svg>
  );

  const modeIcons = { side: <IconSide />, expanded: <IconExpand />, widget: <IconWidget /> };

  return (
    <div
      className={`shape-chat-panel shape-chat-panel--${mode}`}
      role="dialog"
      aria-label={t('chat_title')}
    >
      <div className="shape-chat-header">
        <h3>{t('chat_title')}</h3>
        <div className="shape-chat-header-actions">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className={`shape-chat-mode-btn ${mode === m ? 'is-active' : ''}`}
              onClick={() => setMode(m)}
              title={t(MODE_LABELS[m])}
              aria-label={t(MODE_LABELS[m])}
              aria-pressed={mode === m}
            >
              {modeIcons[m]}
            </button>
          ))}
          <a
            className="shape-chat-header-btn"
            href="https://shapemethod.com/"
            target="_blank"
            rel="noopener noreferrer"
            title={t('chat_open_shape')}
          >
            Shape ↗
          </a>
          <button
            type="button"
            className="shape-chat-header-btn"
            onClick={() => setOpen(false)}
            aria-label={t('chat_close')}
            title={t('chat_close')}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="shape-chat-messages" ref={listRef}>
        {messages.length === 0 && !loading && !picker && (
          <div className="shape-chat-welcome">
            <p style={{ margin: '0 0 14px' }}>
              {t('chat_welcome')}<br />
              {t('chat_welcome_question')}
            </p>
            <div className="shape-chat-suggestions">
              {Object.entries(ACTIONS).map(([key, a]) => (
                <button
                  key={key}
                  type="button"
                  className="shape-chat-suggestion"
                  onClick={() => openPicker(key)}
                >
                  {t(a.label)}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`shape-chat-msg ${m.role}${m.error ? ' error' : ''}`}
          >
            {m.content ? (
              m.role === 'assistant' && !m.error ? (
                <div className="shape-chat-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{m.content}</ReactMarkdown>
                </div>
              ) : (
                m.content
              )
            ) : m.streaming ? (
              <span className="shape-chat-typing">
                <span></span><span></span><span></span>
              </span>
            ) : null}
          </div>
        ))}

        {picker && (
          <div className="shape-chat-picker">
            <div className="shape-chat-picker-header">
              <strong>{t(ACTIONS[picker].label)}</strong>
              <span>{t('chat_pick_survey')}</span>
              <button
                type="button"
                className="shape-chat-picker-cancel"
                onClick={() => setPicker(null)}
              >
                {t('common_cancel')}
              </button>
            </div>
            {loadingSurveys && <div className="shape-chat-picker-hint">{t('chat_loading_surveys')}</div>}
            {!loadingSurveys && surveys?.length === 0 && (
              <div className="shape-chat-picker-hint">{t('chat_no_surveys')}</div>
            )}
            {!loadingSurveys && surveys?.length > 0 && (
              <div className="shape-chat-picker-list">
                {surveys.map((s) => (
                  <button
                    key={s.id_encuesta}
                    type="button"
                    className="shape-chat-picker-item"
                    onClick={() => handlePickSurvey(s)}
                  >
                    <span className="shape-chat-picker-id">#{surveyRef(s)}</span>
                    <span className="shape-chat-picker-title">{s.titulo || t('chat_untitled_item')}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      <form
        className="shape-chat-form"
        onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('chat_placeholder')}
          aria-label={t('chat_placeholder')}
          disabled={loading}
        />
        <button type="submit" disabled={loading || !input.trim()}>
          {t('chat_send')}
        </button>
      </form>
    </div>
  );
};

export default ShapeChat;
